import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewContext, ViewContextKey } from '@core/enums/view-context.enum';
import { RouterHelper } from '@portal-shared/app/helpers/router.helper';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AllyaRouterHelper {
  public static readonly requestIdParam = 'requestId';
  public static readonly assignToParam = 'assignTo';
  public static readonly periodIdParam = 'periodId';
  public static readonly projectIdParam = 'projectId';
  public static readonly draggableDataParam = 'draggable';
  public static readonly backLinkStateParam = 'backNavigationLink';

  public static async setQueryParam(
    router: Router,
    activatedRoute: ActivatedRoute,
    query: {
      [AllyaRouterHelper.requestIdParam]?: string | undefined;
      [AllyaRouterHelper.assignToParam]?: string | undefined;
      [AllyaRouterHelper.periodIdParam]?: string | undefined;
      [AllyaRouterHelper.projectIdParam]?: string | undefined;
    } = {},
    route = ['.'],
    state?: { [k: string]: unknown }
  ) {
    while (activatedRoute.firstChild) {
      activatedRoute = activatedRoute.firstChild;
    }

    await router.navigate(route, {
      relativeTo: activatedRoute,
      queryParams: query,
      queryParamsHandling: 'merge',
      state: state
    });
  }

  public static getCurrentViewContext(): ViewContextKey | undefined {
    const routerUrl = window.location.pathname;
    const viewContextKeys = Object.keys(ViewContext) as ViewContextKey[];
    return viewContextKeys.find(key => routerUrl.includes(`/${ViewContext[key]}`));
  }

  public static isBetaVersionUrl() {
    return window.location.search.includes('beta');
  }

  public static observeRequestId(activatedRoute: ActivatedRoute) {
    return RouterHelper.observeDistinctParam(AllyaRouterHelper.requestIdParam, activatedRoute);
  }

  public static observePeriodId(activatedRoute: ActivatedRoute) {
    return RouterHelper.observeDistinctParam(AllyaRouterHelper.periodIdParam, activatedRoute);
  }

  public static observeProjectId(activatedRoute: ActivatedRoute) {
    return RouterHelper.observeDistinctParam(AllyaRouterHelper.projectIdParam, activatedRoute);
  }
  public static observeAssignTo(activatedRoute: ActivatedRoute) {
    return RouterHelper.observeDistinctParam(AllyaRouterHelper.assignToParam, activatedRoute);
  }

  public constructor(
    public router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  public get(param: string) {
    return RouterHelper.observeDistinctParam(param, this.activatedRoute);
  }

  public getAsync(param: string) {
    return firstValueFrom(this.get(param));
  }
  public getNavigationBackLink(): string | undefined {
    return RouterHelper.getNavigationBackLink(this.router);
  }

  public setQueryParam(
    query: {
      [AllyaRouterHelper.requestIdParam]?: string | undefined;
      [AllyaRouterHelper.assignToParam]?: string | undefined;
      [AllyaRouterHelper.periodIdParam]?: string | undefined;
      [AllyaRouterHelper.projectIdParam]?: string | undefined;
    } = {},
    route = ['.'],
    state?: { [k: string]: unknown }
  ) {
    return AllyaRouterHelper.setQueryParam(this.router, this.activatedRoute, query, route, state);
  }

  public getNavigationBackUrl() {
    return this.router.serializeUrl(
      this.router.createUrlTree([], { queryParams: this.activatedRoute.snapshot.queryParams })
    );
  }
}
